import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';

const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const pad = (n) => String(n).padStart(2, '0');

/** Mini-agenda mensuel : chips d'événements déplaçables en glisser-déposer. */
export default function SepaMiniAgenda({ events = [], onMove, onAdd, onDelete, showPlanRef = false, testid = 'sepa-mini-agenda' }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [addingDate, setAddingDate] = useState(null);
  const [label, setLabel] = useState('RDV');

  const year = month.getFullYear();
  const m = month.getMonth();
  const offset = (new Date(year, m, 1).getDay() + 6) % 7;
  const nbDays = new Date(year, m + 1, 0).getDate();
  const iso = (day) => `${year}-${pad(m + 1)}-${pad(day)}`;
  const byDate = {};
  events.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });

  const shift = (delta) => setMonth(new Date(year, m + delta, 1));
  const monthLabel = month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const submitAdd = () => {
    if (!addingDate || !label.trim()) return;
    onAdd(addingDate, label.trim());
    setAddingDate(null);
    setLabel('RDV');
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <p className="text-[11px] font-bold text-[#0a3d62] uppercase tracking-wider">
          Agenda — RDV &amp; envois de lien <span className="text-slate-400 normal-case">(glisser-déposer pour changer une date)</span>
        </p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => shift(-1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-xs font-semibold text-slate-700 capitalize w-28 text-center">{monthLabel}</span>
          <button type="button" onClick={() => shift(1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] font-bold text-slate-400 pt-2">
        {DAYS.map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 p-2">
        {Array.from({ length: offset }).map((_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: nbDays }, (_, i) => i + 1).map((day) => (
          <div
            key={day}
            className="min-h-[52px] border border-slate-100 rounded-lg p-1 hover:border-[#DEA900]/50 transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const evId = e.dataTransfer.getData('text/plain');
              if (evId) onMove(evId, iso(day));
            }}
            data-testid={`${testid}-day-${iso(day)}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-500 font-semibold">{day}</span>
              <button
                type="button"
                className="text-slate-300 hover:text-[#0a3d62]"
                onClick={() => setAddingDate(iso(day))}
                data-testid={`${testid}-add-${iso(day)}`}
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
            {(byDate[iso(day)] || []).map((ev) => (
              <div
                key={ev.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', ev.id)}
                className="group mt-0.5 flex items-center gap-1 bg-[#0a3d62] text-white rounded px-1 py-0.5 text-[9px] cursor-grab"
                title={showPlanRef ? `${ev.label} — ${ev.souscription_id}` : ev.label}
                data-testid={`${testid}-event-${ev.id}`}
              >
                <span className="truncate flex-1">{showPlanRef ? `${ev.label} · ${String(ev.souscription_id || '').slice(0, 8)}` : ev.label}</span>
                <button type="button" className="opacity-0 group-hover:opacity-100" onClick={() => onDelete(ev)} data-testid={`${testid}-del-${ev.id}`}>
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
      {addingDate && (
        <div className="flex items-center gap-2 px-3 pb-3" data-testid={`${testid}-add-form`}>
          <span className="text-[11px] text-slate-500">{iso(0).slice(0, 8)}{addingDate.slice(8)} :</span>
          <input
            className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="RDV, envoi de lien…"
            data-testid={`${testid}-add-label`}
          />
          <button type="button" onClick={submitAdd} className="text-xs font-bold text-white bg-[#DEA900] rounded px-2 py-1" data-testid={`${testid}-add-submit`}>OK</button>
          <button type="button" onClick={() => setAddingDate(null)} className="text-xs text-slate-400" data-testid={`${testid}-add-cancel`}>Annuler</button>
        </div>
      )}
    </div>
  );
}
